/**
 * What the confirmation view shows and sends — the pure half, kept apart from
 * the component so each rule can be pinned without rendering a table.
 *
 * Nothing here decides a status or counts one: the server does that and says
 * so in each row and in `summary`. This only filters, orders, formats and
 * turns what a person typed into the action the bulk route takes.
 */
import { formatFieldValue } from '@/components/deal/dossier/format'
import {
  buildPatch,
  editableValue,
  fieldValueError,
  type DealFieldDef,
} from '@/services/dealFile'
import type {
  ConfirmationAction,
  ConfirmationReview,
  ConfirmationRow,
  ConfirmationSection,
  ConfirmationStatus,
  ConfirmationSummary,
  ConfirmationView,
} from '@/services/dealConfirmation'

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export type ConfirmationFilter = 'all' | 'missing_required' | 'pending' | 'confirmed' | 'conflicts'

export const CONFIRMATION_FILTERS: ReadonlyArray<{ id: ConfirmationFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'missing_required', label: 'Missing required' },
  { id: 'pending', label: 'Pending' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'conflicts', label: 'Conflicts' },
]

export function matchesFilter(row: ConfirmationRow, filter: ConfirmationFilter): boolean {
  switch (filter) {
    case 'missing_required':
      return row.status === 'missing_required'
    case 'pending':
      return row.status === 'pending'
    case 'confirmed':
      return row.status === 'confirmed'
    case 'conflicts':
      return row.inConflict
    default:
      return true
  }
}

/** The count a filter chip shows, from the server's own summary. */
export function filterCount(summary: ConfirmationSummary, filter: ConfirmationFilter): number {
  switch (filter) {
    case 'missing_required':
      return summary.missingRequiredN
    case 'pending':
      return summary.pendingN
    case 'confirmed':
      return summary.confirmedN
    case 'conflicts':
      return summary.conflictN
    default:
      return summary.totalN
  }
}

/** The search box: the label, the catalogue name, the value as shown, and the
 *  source line. Case-insensitive. */
export function matchesQuery(row: ConfirmationRow, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const hay = [row.label, row.field, valueText(row), row.source.summary, row.group]
  return hay.some((part) => (part ?? '').toLowerCase().includes(needle))
}

/**
 * The rows to draw: filtered, searched, and ordered so what blocks screening
 * leads each section. The order within a section is otherwise the catalogue's,
 * which is the order the screening form asks in — a row that gets filled does
 * not jump to the end of its section, it leaves the missing list.
 *
 * A field that does not apply to this asset class is not offered at all unless
 * asked for: nine hotel lines on an apartment deal are not "missing".
 */
export function visibleSections(
  view: Pick<ConfirmationView, 'sections'>,
  filter: ConfirmationFilter,
  query: string,
  { showNotApplicable = false }: { showNotApplicable?: boolean } = {},
): ConfirmationSection[] {
  const out: ConfirmationSection[] = []
  for (const section of view.sections) {
    const rows = section.rows.filter(
      (row) =>
        (showNotApplicable || row.status !== 'not_applicable') &&
        matchesFilter(row, filter) &&
        matchesQuery(row, query),
    )
    if (rows.length === 0) continue
    const lead = rows.filter((r) => r.status === 'missing_required')
    const rest = rows.filter((r) => r.status !== 'missing_required')
    out.push({ group: section.group, rows: [...lead, ...rest] })
  }
  return out
}

/** The rows "Confirm all visible pending" would confirm: pending, and
 *  something a person may confirm as it stands. Never a conflict, never a
 *  derived output, never an empty field. */
export function confirmablePending(sections: ConfirmationSection[]): ConfirmationRow[] {
  return sections.flatMap((s) => s.rows).filter((row) => row.status === 'pending' && row.confirmable)
}

/** The bulk request for a set of rows. Each action carries the value the
 *  person was looking at, so the server refuses any that has since changed. */
export function confirmActions(rows: ConfirmationRow[]): ConfirmationAction[] {
  return rows.map((row) => ({ field: row.field, action: 'confirm', expectedValue: row.value }))
}

// ---------------------------------------------------------------------------
// Reading a row
// ---------------------------------------------------------------------------

/** The value as a reader expects it, or "" for none. */
export function valueText(row: ConfirmationRow): string {
  if (row.value === null || row.value === undefined || row.value === '') return ''
  if (Array.isArray(row.value)) return row.value.map((v) => String(v)).join(', ')
  if (typeof row.value === 'object') return JSON.stringify(row.value)
  return formatFieldValue(row.value, row.kind, row.field) || String(row.value)
}

/** "42 of 118 confirmed". Derived figures are the platform's arithmetic and
 *  are never confirmed, so they are not in what is being counted toward. */
export function progressText(summary: ConfirmationSummary): string {
  const of = Math.max(summary.totalN - summary.derivedN, 0)
  return `${summary.confirmedN} of ${of} confirmed`
}

/** The share confirmed, 0-100, for the bar. */
export function progressPercent(summary: ConfirmationSummary): number {
  const of = summary.totalN - summary.derivedN
  return of > 0 ? Math.min(100, Math.round((summary.confirmedN / of) * 100)) : 0
}

/** A time the server wrote with no zone is UTC — a SQLite development
 *  database drops the offset a Postgres one keeps — so it is read as UTC rather
 *  than as the browser's local time. */
function asInstant(iso: string): Date {
  const zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso)
  return new Date(!zoned && /^\d{4}-\d{2}-\d{2}T/.test(iso) ? `${iso}Z` : iso)
}

export function stamp(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = asInstant(iso)
  return Number.isNaN(date.getTime())
    ? String(iso)
    : date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export interface StatusMeta {
  label: string
  /** Tailwind classes for the chip. */
  className: string
  dot: string
}

const STATUS_META: Record<ConfirmationStatus, StatusMeta> = {
  pending: {
    label: 'Pending',
    className: 'border-amber-300/30 bg-amber-300/10 text-amber-300',
    dot: 'bg-amber-300',
  },
  confirmed: {
    label: 'Confirmed',
    className: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300',
    dot: 'bg-emerald-400',
  },
  derived: {
    label: 'Derived',
    className: 'border-violet-400/25 bg-violet-400/10 text-violet-300',
    dot: 'bg-violet-400',
  },
  conflicted: {
    label: 'Conflict',
    className: 'border-red-400/30 bg-red-400/10 text-red-300',
    dot: 'bg-red-400',
  },
  missing_required: {
    label: 'Missing · required',
    className: 'border-red-400/30 bg-red-400/10 text-red-300',
    dot: 'bg-red-400',
  },
  missing_optional: {
    label: 'Missing',
    className: 'border-stone-500/30 bg-stone-500/10 text-stone-400',
    dot: 'bg-stone-500',
  },
  not_applicable: {
    label: 'N/A',
    className: 'border-stone-600/30 bg-stone-600/10 text-stone-500',
    dot: 'bg-stone-600',
  },
}

export function statusMeta(status: ConfirmationStatus): StatusMeta {
  return STATUS_META[status]
}

/** The status line under the chip: who confirmed it and when, or what is owed. */
export function statusDetail(row: ConfirmationRow): string {
  if (row.status === 'confirmed' && row.confirmation) {
    const who = row.confirmation.by ?? 'a person'
    const when = stamp(row.confirmation.at)
    return `by ${who}${when ? ` · ${when}` : ''}`
  }
  if (row.status === 'derived') return 'Computed — confirm its inputs'
  if (row.status === 'pending') return 'No person has confirmed this'
  if (row.status === 'missing_required') return 'Needed to screen'
  if (row.status === 'conflicted') return 'Sources disagree'
  if (row.status === 'missing_optional' && row.unreadModels?.length) {
    return `May be in ${row.unreadModels.join(', ')}`
  }
  if (row.status === 'missing_optional' && row.withdrawn) {
    return `Cleared by ${row.withdrawn.by ?? 'a person'}${row.withdrawn.at ? ` · ${stamp(row.withdrawn.at)}` : ''}`
  }
  return ''
}

/** What the manager step says about the deal, or null when it says nothing. */
export function reviewBanner(
  review: ConfirmationReview | undefined,
): { tone: 'wait' | 'returned' | 'approved'; text: string } | null {
  if (!review || !review.enabled || !review.latest) return null
  const latest = review.latest
  const when = stamp(latest.at)
  if (review.state === 'awaiting_manager') {
    return {
      tone: 'wait',
      text: `Awaiting manager approval — submitted by ${latest.actor}${when ? ` · ${when}` : ''}`,
    }
  }
  if (review.state === 'returned') {
    return {
      tone: 'returned',
      text: `Returned by ${latest.actor}${when ? ` · ${when}` : ''}${latest.note ? ` — ${latest.note}` : ''}`,
    }
  }
  if (review.state === 'approved') {
    return { tone: 'approved', text: `Approved by ${latest.actor}${when ? ` · ${when}` : ''}` }
  }
  return null
}

// ---------------------------------------------------------------------------
// Editing a row
// ---------------------------------------------------------------------------

/** The row as the catalogue definition the deal editor's helpers take. */
export function toDef(row: ConfirmationRow): DealFieldDef {
  return {
    name: row.field,
    label: row.label,
    group: row.group,
    kind: row.kind,
    native: row.native,
    unit: row.unit,
    options: row.options,
    derivable: false,
    accelerator: false,
    required: row.required,
    help: row.help,
  }
}

const FIGURE_KINDS = new Set(['currency', 'percent', 'number'])

/** A hand-typed figure carries a one-line reason (owner decision #8). */
export function editNeedsReason(row: ConfirmationRow): boolean {
  return FIGURE_KINDS.has(row.kind)
}

/** What an edit box starts with: the value the way it will be saved. */
export function editDraftOf(row: ConfirmationRow): string {
  return editableValue(toDef(row), row.value)
}

export type BuiltEdit =
  | { ok: true; action: ConfirmationAction }
  | { ok: false; error: string }

/**
 * A typed value as the edit the bulk route takes.
 *
 * Normalised with the deal editor's own `buildPatch`, so "$130.3M", "5.75%" and
 * "1.25x" mean here what they mean in the dossier, and a native percent travels
 * as the fraction the column holds. The server coerces it again exactly as
 * `PUT /loans/{id}` does; this only refuses what can be seen to be wrong early.
 */
export function buildEdit(row: ConfirmationRow, typed: string, reason: string): BuiltEdit {
  const text = typed.trim()
  if (!text) return { ok: false, error: `${row.label}: enter a value, or use Clear to remove it.` }
  const def = toDef(row)
  const error = fieldValueError(def, text)
  if (error) return { ok: false, error }
  const patch = buildPatch([def], undefined, { [row.field]: text })
  const value = row.native ? patch[row.field] : patch.custom_fields?.[row.field]
  if (value === undefined || value === null) {
    return { ok: false, error: `${row.label}: that is not a value.` }
  }
  const why = reason.trim()
  if (editNeedsReason(row) && !why) {
    return { ok: false, error: `A one-line reason is required to hand-type ${row.label}.` }
  }
  return {
    ok: true,
    action: {
      field: row.field,
      action: 'edit',
      value,
      expectedValue: row.value ?? null,
      ...(why ? { reason: why } : {}),
    },
  }
}

export function clearAction(row: ConfirmationRow, reason?: string): ConfirmationAction {
  return {
    field: row.field,
    action: 'clear',
    expectedValue: row.value ?? null,
    ...(reason?.trim() ? { reason: reason.trim() } : {}),
  }
}
